
import React,{ useEffect } from 'react';
import './live-dashboard.scss';
import { LiveDashboardService } from '../../core/services/live-dashboard.service';
import { useNavigate, useLocation } from 'react-router-dom';

// Per-platform app analytics (GA4 / Firebase), delivered on the same payload as
// the signup counts. `windows` only carries a key for a range GA4 returned rows
// for, so "today" is simply absent until the first event of the day lands —
// every read below defaults rather than assuming the key exists.
const EMPTY_BUCKET = { installs: 0, newUsers: 0, activeUsers: 0, sessions: 0 };

// Categorical slots 1 and 2 of the validated palette. Two series only, so the
// adjacent pair is the whole palette: CVD ΔE 24.7, normal-vision ΔE 33.6 —
// clear of the ≥8 / ≥15 floors on a white card.
const IOS_COLOR = '#2a78d6';
const ANDROID_COLOR = '#eb6834';
// Total is an aggregate of the two, not a third category, so it wears a neutral
// ink rather than a third hue — that keeps the two real series the only colours
// competing for attention and sidesteps a 3-hue CVD check that would exist only
// to label a subtotal.
const TOTAL_COLOR = '#5d5d5d';

const PLATFORM_RANGES: { [key: string]: { label: string; from: string; to: string } } = {
  today: { label: 'Today', from: 'today', to: 'today' },
  yesterday: { label: 'Yesterday', from: 'yesterday', to: 'yesterday' },
  // 'lifetime' is a token, not a date: the backend maps it to an unbounded
  // SQL window and to GA4's earliest available date separately.
  lifetime: { label: 'Lifetime', from: 'lifetime', to: 'today' },
};

const nf = new Intl.NumberFormat('en-IN');
const fmt = (n: any) => nf.format(Math.round(Number(n) || 0));

// Seconds -> "4h 52m" / "3m 20s". Raw seconds are unreadable at these sizes.
const fmtDuration = (secs: number) => {
  const s = Math.max(0, Math.round(Number(secs) || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s % 60}s`;
  return `${s}s`;
};

const fmtDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

// GA4 event names are developer strings. The dashboard is read by people who
// have never seen them, so each is given a plain-English name and a sentence
// saying what it actually counts.
const EVENT_LABELS: { [name: string]: { label: string; help: string } } = {
  first_open: { label: 'First app opens', help: 'A fresh install being opened — the closest thing to an install count.' },
  screen_view: { label: 'Screens viewed', help: 'Every time a screen appears. High numbers are normal.' },
  user_engagement: { label: 'Active-use pings', help: 'Logged periodically while the app is genuinely in the foreground.' },
  session_start: { label: 'Sessions started', help: 'A visit to the app. A new one starts after 30 minutes idle.' },
  app_update: { label: 'App updated', help: 'An existing user moved to a newer version.' },
  os_update: { label: 'Phone OS upgrades', help: 'The device operating system was upgraded.' },
  in_app_purchase: { label: 'In-app purchases', help: 'A purchase completed inside the app.' },
  app_store_subscription_renew: { label: 'Subscriptions renewed', help: 'An App Store subscription auto-renewed.' },
  app_store_subscription_convert: { label: 'Trials converted', help: 'A free trial became a paid subscription.' },
  app_remove: { label: 'App uninstalled', help: 'Android only — Apple does not report uninstalls.' },
  app_exception: { label: 'App crashes', help: 'The app stopped unexpectedly.' },
};

const KPI_HELP: { [k: string]: string } = {
  installs: 'First-time app opens in this period. Store downloads run 5–15% higher, because not everyone opens what they download.',
  newUsers: 'People using the app for the first time.',
  activeUsers: 'Distinct people who opened the app at least once.',
  sessions: 'Visits to the app. One person can have many.',
  engagement: 'Average time each active person spent actually using the app across the whole period.',
  engagedRate: 'Share of visits that lasted over 10 seconds or went beyond one screen — i.e. real use, not an accidental open.',
  screensPerSession: 'How many screens a typical visit moves through. Higher means people are exploring further.',
  revenue: 'Money from in-app purchases and subscriptions, as reported by the stores.',
  returning: 'People who had used the app before and came back in this period. GA4 counts someone as both new and returning if they did both here, so these do not add up to the active-user total.',
};

// Info affordance. The native `title` attribute was the first attempt and was
// wrong for this: it waits ~1s, renders at the pointer rather than the icon, and
// a stale tip from a neighbouring control can sit over an unrelated card — which
// is exactly what it looked like on the iOS vs Android header. This renders its
// own bubble anchored to the icon, on hover AND keyboard focus.
const Info: React.FC<{ text: string }> = ({ text }) => (
  <span className="info" tabIndex={0} role="note" aria-label={text}>
    <span className="info-dot" aria-hidden="true">?</span>
    <span className="info-bubble">{text}</span>
  </span>
);

const KpiTile: React.FC<{ label: string; value: string; help: string; sub?: string }> =
  ({ label, value, help, sub }) => (
    <div className="kpi-tile">
      <div className="kpi-label">{label}<Info text={help} /></div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );

// Stacked share bar: one row, iOS vs Android, direct-labelled. A 2px surface
// gap separates the segments so the boundary survives without relying on hue.
const SplitBar: React.FC<{ ios: number; android: number; unit: string }> = ({ ios, android, unit }) => {
  const total = ios + android;
  if (!total) return <div className="geo-empty">No {unit} in this period.</div>;
  const iosPct = (ios / total) * 100;
  return (
    <div className="split">
      <div className="split-bar" role="img"
           aria-label={`iOS ${ios} ${unit}, Android ${android} ${unit}`}>
        {ios > 0 && <i style={{ width: `${iosPct}%`, background: IOS_COLOR }} />}
        {android > 0 && <i style={{ width: `${100 - iosPct}%`, background: ANDROID_COLOR }} />}
      </div>
      <div className="split-legend">
        <span><b style={{ background: IOS_COLOR }} />iOS <strong>{fmt(ios)}</strong> ({Math.round(iosPct)}%)</span>
        <span><b style={{ background: ANDROID_COLOR }} />Android <strong>{fmt(android)}</strong> ({Math.round(100 - iosPct)}%)</span>
        <span className="split-total"><b style={{ background: TOTAL_COLOR }} />Total <strong>{fmt(total)}</strong></span>
      </div>
    </div>
  );
};

/**
 * Installs per day, as an area + line with a hover crosshair.
 *
 * Inline SVG rather than a charting dependency: it is one series on one axis,
 * and adding a library to this bundle for that would cost more than it returns.
 * Long ranges are bucketed to weeks — 494 daily points across a lifetime range
 * renders as noise, and no reader can pick a day out of it anyway.
 */
const TrendChart: React.FC<{ daily: any[]; compare?: boolean }> = ({ daily, compare }) => {
  const [hover, setHover] = React.useState<number | null>(null);

  const { points, unit } = React.useMemo(() => {
    const rows = daily || [];
    if (rows.length <= 92) {
      return { points: rows.map((r: any) => ({ ...r, label: fmtDate(r.date) })), unit: 'day' };
    }
    const weeks: any[] = [];
    for (let i = 0; i < rows.length; i += 7) {
      const chunk = rows.slice(i, i + 7);
      const add = (k: string) => chunk.reduce((a: number, r: any) => a + (r[k] || 0), 0);
      weeks.push({
        date: chunk[0].date,
        label: `week of ${fmtDate(chunk[0].date)}`,
        installs: add('installs'),
        activeUsers: add('activeUsers'),
        ios: add('ios'),
        android: add('android'),
      });
    }
    return { points: weeks, unit: 'week' };
  }, [daily]);

  if (points.length < 2) return <div className="geo-empty">Not enough days in this period to draw a trend.</div>;

  const W = 720, H = 190, PAD_L = 38, PAD_B = 22, PAD_T = 10;
  const max = Math.max(1, ...points.map((p: any) => p.installs));
  const x = (i: number) => PAD_L + (i * (W - PAD_L - 8)) / (points.length - 1);
  const y = (v: number) => PAD_T + (1 - v / max) * (H - PAD_T - PAD_B);

  const pathFor = (k: string) =>
    points.map((p: any, i: number) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p[k] || 0).toFixed(1)}`).join(' ');
  const line = pathFor('installs');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)} Z`;
  // Only draw a platform line when that platform actually has data in range —
  // a flat zero line reads as a real measurement rather than an absence.
  const hasIos = compare && points.some((p: any) => (p.ios || 0) > 0);
  const hasAndroid = compare && points.some((p: any) => (p.android || 0) > 0);
  const ticks = [0, Math.round(max / 2), max].filter((v, i, a) => a.indexOf(v) === i);
  const active = hover === null ? null : points[hover];

  return (
    <div className="trend">
      <svg viewBox={`0 0 ${W} ${H}`} className="trend-svg" role="img"
           aria-label={`Installs per ${unit} over the selected period`}
           onMouseLeave={() => setHover(null)}
           onMouseMove={(e) => {
             const box = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
             const px = ((e.clientX - box.left) / box.width) * W;
             const i = Math.round(((px - PAD_L) / (W - PAD_L - 8)) * (points.length - 1));
             setHover(Math.max(0, Math.min(points.length - 1, i)));
           }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD_L} x2={W - 8} y1={y(t)} y2={y(t)} className="trend-grid" />
            <text x={PAD_L - 8} y={y(t) + 4} className="trend-tick" textAnchor="end">{fmt(t)}</text>
          </g>
        ))}
        <path d={area} className="trend-area" />
        {/* Total sits underneath as the neutral envelope; the two platform
            lines draw over it so a reader can see who drove each peak. */}
        <path d={line} className="trend-line" style={{ stroke: compare ? TOTAL_COLOR : IOS_COLOR }} />
        {hasIos && <path d={pathFor('ios')} className="trend-line trend-line--thin" style={{ stroke: IOS_COLOR }} />}
        {hasAndroid && <path d={pathFor('android')} className="trend-line trend-line--thin" style={{ stroke: ANDROID_COLOR }} />}
        {active && (
          <g>
            <line x1={x(hover!)} x2={x(hover!)} y1={PAD_T} y2={H - PAD_B} className="trend-cross" />
            <circle cx={x(hover!)} cy={y(active.installs)} r="5" className="trend-dot" />
          </g>
        )}
        <text x={PAD_L} y={H - 6} className="trend-tick">{points[0].label}</text>
        <text x={W - 8} y={H - 6} className="trend-tick" textAnchor="end">{points[points.length - 1].label}</text>
      </svg>
      {compare &&
        <div className="split-legend trend-legend">
          <span><b style={{ background: TOTAL_COLOR }} />Total</span>
          <span><b style={{ background: IOS_COLOR }} />iOS</span>
          <span><b style={{ background: ANDROID_COLOR }} />Android</span>
        </div>}
      <div className="trend-readout">
        {active
          ? <>
              <strong>{fmt(active.installs)}</strong> install{active.installs === 1 ? '' : 's'} · {active.label}
              {compare && <em className="trend-readout-split">
                {' '}· iOS {fmt(active.ios || 0)} · Android {fmt(active.android || 0)}
              </em>}
            </>
          : <>Hover the chart to read any {unit}</>}
      </div>
    </div>
  );
};

// Sequential blue ramp (one hue, light -> dark) for magnitude. Sequential
// encoding never uses multiple hues; the lightest step means "near zero".
const RETENTION_RAMP = ['#eef4fd', '#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#2a78d6', '#256abf', '#184f95'];
const retentionColor = (pct: number | null) => {
  if (pct === null) return 'transparent';
  const i = Math.min(RETENTION_RAMP.length - 1, Math.floor((pct / 100) * RETENTION_RAMP.length));
  return RETENTION_RAMP[i];
};

/**
 * Retention grid: of the people who first opened the app in a given week, what
 * share came back in each later week. Read left to right along a row.
 */
const RetentionGrid: React.FC<{ retention: any }> = ({ retention }) => {
  if (!retention || !retention.rows?.length) {
    return <div className="geo-empty">Not enough history yet to measure retention.</div>;
  }
  const weeks = retention.weeks || 6;
  return (
    <div className="retention">
      <div className="retention-scroll">
        <table className="retention-table">
          <thead>
            <tr>
              <th>Week they joined</th>
              <th className="retention-size">People</th>
              {Array.from({ length: weeks }, (_, w) => <th key={w}>{w === 0 ? 'Joined' : `Wk ${w}`}</th>)}
            </tr>
          </thead>
          <tbody>
            {retention.rows.map((r: any) => (
              <tr key={r.label}>
                <td className="retention-label">{fmtDate(r.label)}</td>
                <td className="retention-size">{fmt(r.size)}</td>
                {r.weeks.slice(0, weeks).map((v: number | null, w: number) => (
                  <td key={w} className="retention-cell">
                    {v === null
                      ? <span className="retention-na" title="This group is not old enough to have reached this week yet.">–</span>
                      : <span className="retention-chip"
                              style={{ background: retentionColor(v), color: v > 55 ? '#fff' : '#333' }}
                              title={`${Math.round((v / 100) * r.size)} of ${r.size} people came back`}>
                          {v}%
                        </span>}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="retention-avg">
              <td className="retention-label">Average</td>
              <td className="retention-size">—</td>
              {retention.average.slice(0, weeks).map((v: number | null, w: number) => (
                <td key={w} className="retention-cell"><strong>{v === null ? '–' : `${v}%`}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="platform-hint">
        Each row is a group of people who installed in the same week. "Wk 1" is how many of them opened
        the app again the week after. Dropping steeply is normal for apps; the number to watch is whether
        newer rows hold up better than older ones.
      </div>
    </div>
  );
};

// Last 30 minutes of activity, one bar per minute.
const LiveMinutes: React.FC<{ byMinute: any[]; total: number }> = ({ byMinute, total }) => {
  if (!byMinute?.length) return null;
  const max = Math.max(1, ...byMinute.map((m: any) => m.users));
  return (
    <div className="live">
      <div className="live-head">
        <span className="live-dot" aria-hidden="true" />
        <strong>{fmt(total)}</strong> active in the last 30 minutes
      </div>
      <div className="live-bars" role="img" aria-label={`Active users per minute over the last 30 minutes, peak ${max}`}>
        {byMinute.map((m: any) => (
          <i key={m.minutesAgo}
             style={{ height: `${m.users ? Math.max((m.users / max) * 100, 8) : 2}%` }}
             title={`${m.users} active · ${m.minutesAgo === 0 ? 'this minute' : `${m.minutesAgo} min ago`}`} />
        ))}
      </div>
      <div className="live-axis"><span>30 min ago</span><span>now</span></div>
    </div>
  );
};

// Ranked list with a magnitude bar — used for geo, devices, versions, events.
// Relative "updated" stamp. Absolute clock times make a viewer work out
// freshness themselves; "2 min ago" is the question they actually have.
const sinceLabel = (d: Date | null) => {
  if (!d) return '';
  const secs = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  return `${hrs}h ago`;
};

// First-load placeholder. Shaped like the real content (tiles, then two cards)
// so the page does not jump when the data lands.
const PlatformSkeleton: React.FC = () => (
  <div className="skeleton" aria-busy="true" aria-live="polite">
    <span className="sr-only">Loading app analytics…</span>
    <div className="kpi-grid">
      {Array.from({ length: 8 }, (_, i) => (
        <div className="kpi-tile" key={i}>
          <div className="sk sk-line sk-sm" />
          <div className="sk sk-line sk-lg" />
          <div className="sk sk-line sk-xs" />
        </div>
      ))}
    </div>
    <div className="chart-grid">
      <div className="chart-card"><div className="sk sk-line sk-sm" /><div className="sk sk-block" /></div>
      <div className="chart-card"><div className="sk sk-line sk-sm" /><div className="sk sk-block sk-block--short" /></div>
    </div>
  </div>
);

// Platform split for the DATABASE-side numbers, as distinct from the Firebase
// figures further down. Web wears the neutral ink: it is the original platform
// and holds almost all the volume, so a third saturated hue would fight the
// iOS/Android pair for attention without adding information.
const WEB_COLOR = TOTAL_COLOR;

// One signup bucket -> one number. The three tiers (captured at signup,
// inferred from a push token, assumed because nothing was recorded) are kept
// apart on the wire so the card can footnote how solid a number is, but the
// headline count is all of them. Leaving `assumed` out is what made Web read
// as 1 signup against 94k accounts.
const signupTotal = (b: any) =>
  (b?.captured || 0) + (b?.inferred || 0) + (b?.assumed || 0);

const PlatformSplitCard: React.FC<{
  title: string;
  help: string;
  rows: { key: string; label: string; color: string; value: number; note?: string }[];
  footnote?: string;
}> = ({ title, help, rows, footnote }) => {
  const total = rows.reduce((a, r) => a + (r.value || 0), 0);
  return (
    <div className="geo-list">
      <div className="geo-list-title">{title}<Info text={help} /></div>
      <table className="rank-table">
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td className="rank-name">
                <span className="split-dot" style={{ background: r.color }} />
                <span className="geo-name-text">{r.label}</span>
              </td>
              <td className="rank-num rank-total">{fmt(r.value)}</td>
              <td className="rank-share">{total ? `${Math.round((r.value / total) * 1000) / 10}%` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.some((r) => r.note) &&
        <div className="geo-note">{rows.filter((r) => r.note).map((r) => `${r.label}: ${r.note}`).join(' · ')}</div>}
      {footnote && <div className="geo-note">{footnote}</div>}
    </div>
  );
};

const RankedList: React.FC<{ title: string; help?: string; rows: any[]; valueKey?: string; unit?: string; compare?: boolean; describe?: (name: string) => string | undefined }> =
  ({ title, help, rows, valueKey = 'installs', unit, compare, describe }) => (
    <div className="geo-list">
      <div className="geo-list-title">{title}{help && <Info text={help} />}</div>
      {rows && rows.length > 0 ? (
        // A real table, not a grid per row. Each row used to be its own grid
        // with auto-sized columns, so every row (and the header) resolved its
        // widths independently and nothing lined up. A table shares one set of
        // column widths across every row by definition.
        <table className="rank-table">
          {compare && (
            <thead>
              <tr>
                <th />
                <th className="rank-num" style={{ color: IOS_COLOR }}>iOS</th>
                <th className="rank-num" style={{ color: ANDROID_COLOR }}>Android</th>
                <th className="rank-num">Total</th>
                <th className="rank-share">Share</th>
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.name}>
                <td className="rank-name">
                  <span className="geo-name-text" title={r.name}>{r.name}</span>
                  {describe && describe(r.name) && <Info text={describe(r.name) as string} />}
                </td>
                {compare ? (
                  <>
                    <td className="rank-num" style={{ color: IOS_COLOR }}>{fmt(r.ios || 0)}</td>
                    <td className="rank-num" style={{ color: ANDROID_COLOR }}>{fmt(r.android || 0)}</td>
                  </>
                ) : (
                  <td className="rank-bar">
                    <span className="geo-bar"><i style={{ width: `${Math.max(r.share, 1)}%` }} /></span>
                  </td>
                )}
                <td className="rank-num rank-total">{fmt(r[valueKey] ?? r.value)}</td>
                <td className="rank-share">{r.share}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div className="geo-empty">No {unit || 'data'} in this period.</div>}
    </div>
  );

const LiveDashboard: React.FC = () => {

    const navigate = useNavigate();
    const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get('sessionId');

  const [liveData, setLiveData] = React.useState<any>([]);
  const loadRef = React.useRef<((opts?: { showLoader?: boolean; refresh?: boolean }) => void) | null>(null);
  // `platforms` is a SIBLING of `rows` on the response, not a column inside the
  // counts row — it comes from GA4, not from the SQL aggregate. Holding it in
  // its own state (rather than reading liveData[0].platforms) is what makes the
  // App Platforms section render at all.
  const [platforms, setPlatforms] = React.useState<any>(null);
  // Siblings of `rows` on the response, not columns inside the counts row.
  const [byPlatform, setByPlatform] = React.useState<any>(null);
  // Lifetime by default: 'today' is near-empty for app data because GA4
  // reporting lags ~24h, so the page opened on a wall of zeros.
  const [rangeKey, setRangeKey] = React.useState<string>('lifetime');
  const showingAllTime = rangeKey === 'lifetime';
  const [platformTab, setPlatformTab] = React.useState<'all' | 'ios' | 'android'>('all');
  // Only user-initiated fetches (first load, range change, Refresh) raise the
  // loader. The 60s background poll must not flash a spinner over the page.
  const [platformLoading, setPlatformLoading] = React.useState<boolean>(true);
  const [lastUpdated, setLastUpdated] = React.useState<Date | null>(null);
  const [customFrom, setCustomFrom] = React.useState<string>('');
  const [customTo, setCustomTo] = React.useState<string>('');

  // The active window: a preset, or the custom range once BOTH ends are set.
  const activeRange = React.useMemo(() => {
    if (rangeKey === 'custom') {
      return customFrom && customTo ? { from: customFrom, to: customTo } : null;
    }
    return PLATFORM_RANGES[rangeKey];
  }, [rangeKey, customFrom, customTo]);

  useEffect(() => {
    // One loader for both the initial fetch and the interval: the two copies
    // that used to exist are how the platforms binding got missed in one place.
    const load = (opts?: { showLoader?: boolean; refresh?: boolean }) => {
      if (opts?.showLoader) setPlatformLoading(true);
      LiveDashboardService.getLiveDashboardData(sessionId, activeRange ?? undefined, opts?.refresh)
        .then((res: any) => {
          const data = res?.data?.rows || [];
          if (data.length === 0) {
            navigate('/');
          } else {
            setLiveData(data);
            setPlatforms(res?.data?.platforms ?? null);
            setByPlatform({
              subs: res?.data?.subsByPlatform ?? null,
              signups: res?.data?.signupsByPlatform ?? null,
            });
            setLastUpdated(new Date());
          }
        })
        .catch((e: any) => setLiveData([]))
        .finally(() => setPlatformLoading(false));
    };
    // Exposed so the Refresh button can force a live fetch past the GA4 cache.
    loadRef.current = load;

    load({ showLoader: true });

    // OLD: 5000 — the aggregate costs ~1.1s of prod DB time, so a 5s poll held
    // the DB busy ~22% of the time per open tab.
    const intervalId = setInterval(() => load(), 60000);

    // Clean up the interval when the component is unmounted
    return () => clearInterval(intervalId);
    // Re-runs on range change so switching filter refetches immediately
    // instead of waiting out the poll. Depending on the two primitive ends
    // rather than the `activeRange` object keeps this to one refetch per real
    // change; `navigate` and `sessionId` are stable for the page's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRange?.from, activeRange?.to]);

  return (
    <>
    {liveData.length === 0 && platformLoading &&
      <section className="subscriber-count-sec">
        <div className="container">
          <div className="page-loading" aria-busy="true" aria-live="polite">
            <span className="page-loading-spinner" aria-hidden="true" />
            Loading live dashboard…
          </div>
        </div>
      </section>
    }
    {liveData.length > 0 &&
    <section className="subscriber-count-sec">
    <div className="container">
      {/* Both halves of this page come from ONE request, so this button and the
          one in the App Platforms filter bar do the same thing — refetch
          everything, bypassing the GA4 cache. It is repeated here simply so a
          reader at the top of the page does not have to scroll to find it. */}
      {/* One toolbar for the whole page: the range now drives the signup and
          subscription counts as well as the Firebase figures, so it no longer
          belongs inside the App Platforms block. */}
      <div className="page-toolbar platform-filters">
          {Object.keys(PLATFORM_RANGES).map((key) => (
          <button
            key={key}
            type="button"
            className={`platform-filter${rangeKey === key ? ' is-active' : ''}`}
            onClick={() => setRangeKey(key)}
          >
            {PLATFORM_RANGES[key].label}
          </button>
        ))}
        <button
          type="button"
          className={`platform-filter${rangeKey === 'custom' ? ' is-active' : ''}`}
          onClick={() => setRangeKey('custom')}
        >
          Date range
        </button>

        {rangeKey === 'custom' &&
          <span className="platform-filter-dates">
            <input
              type="date"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
            <span>to</span>
            <input
              type="date"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => setCustomTo(e.target.value)}
            />
          </span>
        }
        <span className="platform-filter-spacer" />
        <span className="platform-updated">
          {platformLoading ? 'Updating…' : lastUpdated ? `Updated ${sinceLabel(lastUpdated)}` : ''}
        </span>
        <button
          type="button"
          className="platform-refresh"
          onClick={() => loadRef.current?.({ showLoader: true, refresh: true })}
          disabled={platformLoading}
          title="Reload everything on this page: the signup and subscription counts, and live numbers from Google Analytics."
        >
          <span className={`platform-refresh-icon${platformLoading ? ' is-spinning' : ''}`} aria-hidden="true">⟳</span>
          {platformLoading ? 'Refreshing' : 'Refresh'}
        </button>
      </div>
      <div className={`counts-area${platformLoading ? ' is-refreshing' : ''}`}>
      <div className="total-user-and-active-subscription">
        <div className="subscriber-count-total">
          Total Users
          <strong id="total-users-count">{liveData[0]?.total_users}</strong>
          {/* A running total has no period — it is the same number whatever
              range is picked, so it says so instead of appearing frozen. */}
          <small>all time{!showingAllTime && <> · {fmt(liveData[0]?.period_users)} joined in this period</>}</small>
        </div>
        <div className="subscriber-count-total">
          Active Subscriptions
          <strong id="total-active-subscription-count">
            {showingAllTime ? liveData[0]?.active_subscriptions : liveData[0]?.period_subscriptions}
          </strong>
          {!showingAllTime && <small>started in this period · {fmt(liveData[0]?.active_subscriptions)} active now</small>}
          {/* Free trials and admin-granted comp plans are real active plans but not
              revenue, so they are reported beside the count instead of inside it. */}
          {showingAllTime && (liveData[0]?.active_trial > 0 || liveData[0]?.active_comp > 0) &&
            <small id="active-subscription-note">
              plus {liveData[0]?.active_trial} on trial
              {liveData[0]?.active_comp > 0 && <>, {liveData[0]?.active_comp} complimentary</>}
            </small>
          }
        </div>
      </div>
      <div className="row w-100 mb-4" >
        <strong className="title-w-bdr"><span>{showingAllTime ? 'Signup (all time)' : 'Signup in this period'}</span></strong>
        <div className="row w-100 mx-auto">
          <div className="other-signup-data">
            <div className="other-signup-data-main">
              <span>Brand</span><strong id="brand-count">{showingAllTime ? liveData[0]?.total_brand : liveData[0]?.period_brand}</strong>
            </div>
            <div className="other-signup-data-split">
              <span>Free <strong id="brand-count-free">{showingAllTime
                  ? liveData[0]?.total_brand - liveData[0]?.brand_paid
                  : liveData[0]?.period_brand - liveData[0]?.period_brand_paid}</strong></span>
              <span>Paid<strong id="brand-count-paid">{showingAllTime ? liveData[0]?.brand_paid : liveData[0]?.period_brand_paid}</strong></span>
            </div>
          </div>
          <div className="other-signup-data">
            <div className="other-signup-data-main">
              <span>Influencer</span><strong id="influencer-count">{showingAllTime ? liveData[0]?.total_influencer : liveData[0]?.period_influencer}</strong>
            </div>
            <div className="other-signup-data-split">
              <span>Free <strong id="influencer-count-free">{showingAllTime
                  ? liveData[0]?.total_influencer - liveData[0]?.influencer_paid
                  : liveData[0]?.period_influencer - liveData[0]?.period_influencer_paid}</strong></span>
              {/* OLD: Paid had a hardcoded +818 added to it — subscriptions belonging to
                  pre-migration users that were never carried into this DB. Free was
                  computed WITHOUT the offset, so Free + Paid overshot the Influencer
                  total by exactly 818 and the card never reconciled. The offset is a
                  frozen two-year-old number against a live one, so it is gone; the
                  tile now reports only what this database can actually account for.
                  <span>Paid<strong id="influencer-count-paid">{(liveData[0]?.influencer_paid) + (818)}</strong></span> */}
              <span>Paid<strong id="influencer-count-paid">{showingAllTime ? liveData[0]?.influencer_paid : liveData[0]?.period_influencer_paid}</strong></span>
            </div>
          </div>
          <div className="other-signup-data">
            <div className="other-signup-data-main">
              <span>Agency</span><strong id="agency-count">{showingAllTime ? liveData[0]?.total_agency : liveData[0]?.period_agency}</strong>
            </div>
            <div className="other-signup-data-split">
              <span>Free <strong id="agency-count-free">{showingAllTime
                  ? liveData[0]?.total_agency - liveData[0]?.agency_paid
                  : liveData[0]?.period_agency - liveData[0]?.period_agency_paid}</strong></span>
              <span>Paid<strong id="agency-count-paid">{showingAllTime ? liveData[0]?.agency_paid : liveData[0]?.period_agency_paid}</strong></span>
            </div>
          </div>
          <div className="other-signup-data">
            <div className="other-signup-data-main">
              <span>Talent Partner</span><strong id="talent-count">{showingAllTime ? liveData[0]?.total_talent_partner : liveData[0]?.period_talent_partner}</strong>
            </div>
            <div className="other-signup-data-split">
              <span>Free <strong id="talent-count-free">{showingAllTime
                  ? liveData[0]?.total_talent_partner - liveData[0]?.talent_partner_paid
                  : liveData[0]?.period_talent_partner - liveData[0]?.period_talent_partner_paid}</strong></span>
              <span>Paid<strong id="talent-count-paid">{showingAllTime ? liveData[0]?.talent_partner_paid : liveData[0]?.period_talent_partner_paid}</strong></span>
            </div>
          </div>
        </div>
      </div>
       {/* Total Subscriptions */}
      <div className="row w-100 mb-5">
        <strong className="title-w-bdr"><span>{showingAllTime ? 'Active Subscriptions' : 'Subscriptions started in this period'}</span></strong>
        <div className="row w-100 mx-auto">
          <div className="other-signup-data">
            <div className="other-signup-data-main">
              <span>Brand</span><strong id="total-subscription-count-brand">{showingAllTime ? liveData[0]?.active_brand : liveData[0]?.period_sub_brand}</strong>
            </div>
          </div>
          <div className="other-signup-data">
            <div className="other-signup-data-main">
              <span>Influencer</span><strong id="total-subscription-count-influencer">{showingAllTime ? liveData[0]?.active_influencer : liveData[0]?.period_sub_influencer}</strong>
            </div>
          </div>
          <div className="other-signup-data">
            <div className="other-signup-data-main">
              <span>Agency</span><strong id="total-subscription-count-agency">{showingAllTime ? liveData[0]?.active_agency : liveData[0]?.period_sub_agency}</strong>
            </div>
          </div>
          <div className="other-signup-data">
            <div className="other-signup-data-main">
              <span>Talent Partner</span><strong id="total-subscription-count-talent">{showingAllTime ? liveData[0]?.active_talent_partner : liveData[0]?.period_sub_talent_partner}</strong>
            </div>
          </div>
        </div>
      </div>
      {/* Platform split of the DATABASE-side numbers. Kept inside the counts
          area, above the Firebase block, because these come from our own
          tables and respect the same date filter. */}
      {(byPlatform?.subs || byPlatform?.signups) &&
      <div className="row w-100 mb-5">
        <strong className="title-w-bdr"><span>By platform</span></strong>
        <div className="geo-grid geo-grid--pair">
          {byPlatform?.subs &&
            <PlatformSplitCard
              title="Paying subscribers"
              help="Which app store or site the plan was bought on. Taken from the plan the user checked out with — the apps request plans for their own platform, so an Android user paying through the web page still counts as Android."
              rows={[
                { key: 'web', label: 'Web', color: WEB_COLOR, value: byPlatform.subs.web?.paid || 0 },
                { key: 'ios', label: 'iOS', color: IOS_COLOR, value: byPlatform.subs.ios?.paid || 0 },
                { key: 'android', label: 'Android', color: ANDROID_COLOR, value: byPlatform.subs.android?.paid || 0,
                  note: (byPlatform.subs.android?.paid || 0) === 0 ? 'no Android subscription yet' : undefined },
              ]}
              footnote={`Currently active — Web ${fmt(byPlatform.subs.web?.active)} · iOS ${fmt(byPlatform.subs.ios?.active)} · Android ${fmt(byPlatform.subs.android?.active)}`}
            />}

          {byPlatform?.signups
            ? <PlatformSplitCard
                title="Signups by platform"
                help="Where each account was created. Values recorded by the app or site at signup are exact; anything marked estimated was inferred from a push token during the one-off backfill. Accounts with nothing recorded count as web, which is the default and was the only way to sign up before the apps existed."
                rows={[
                  // OLD — each row added captured + inferred only, so the
                  // ~94k accounts that carry neither (everything created
                  // before the signup_platform column shipped) were counted
                  // nowhere and Web read as 1. `assumed` is the backend's
                  // web-by-default tier and is now part of the total.
                  // { key: 'web', label: 'Web', color: WEB_COLOR,
                  //   value: (byPlatform.signups.web?.captured || 0) + (byPlatform.signups.web?.inferred || 0) },
                  { key: 'web', label: 'Web', color: WEB_COLOR,
                    value: signupTotal(byPlatform.signups.web),
                    note: byPlatform.signups.web?.assumed ? `${fmt(byPlatform.signups.web.assumed)} assumed (no platform recorded)` : undefined },
                  { key: 'ios', label: 'iOS', color: IOS_COLOR,
                    value: signupTotal(byPlatform.signups.ios),
                    note: byPlatform.signups.ios?.inferred ? `${fmt(byPlatform.signups.ios.inferred)} estimated` : undefined },
                  { key: 'android', label: 'Android', color: ANDROID_COLOR,
                    value: signupTotal(byPlatform.signups.android),
                    note: byPlatform.signups.android?.inferred ? `${fmt(byPlatform.signups.android.inferred)} estimated` : undefined },
                  // Nothing lands here any more — the backend folds unrecorded
                  // accounts into web — so the row is dropped unless an older
                  // API build is serving this page and still fills it.
                  ...(signupTotal(byPlatform.signups.unknown)
                    ? [{ key: 'unknown', label: 'Not recorded', color: '#c9c9c9',
                         value: signupTotal(byPlatform.signups.unknown) }]
                    : []),
                  // OLD — 'Not recorded' was always rendered, and always 0.
                  // { key: 'unknown', label: 'Not recorded', color: '#c9c9c9',
                  //   value: (byPlatform.signups.unknown?.captured || 0) + (byPlatform.signups.unknown?.inferred || 0) },
                ]}
                footnote="Accounts created before platform tracking existed carry no platform, so they count as web — the default, and the only signup route before the apps launched."
              />
            : <div className="geo-list">
                <div className="geo-list-title">Signups by platform</div>
                <div className="geo-empty">Not available yet — the signup_platform migration has not been run on this database.</div>
              </div>}
        </div>
      </div>
      }
      {/* Per-platform app analytics, last on the page. Hidden entirely when GA4
          is unreachable or unconfigured so the page never shows an empty shell. */}
      </div>

      {(platforms || platformLoading) &&
      <div className={`row w-100 mb-5${platformLoading && platforms?.views ? ' is-refreshing' : ''}`}>
        <strong className="title-w-bdr"><span>App Platforms</span></strong>


        {/* Platform tabs. Every block below reads from the selected slice, so
            iOS and Android get dedicated geo, devices, versions and events —
            not just a split of the headline number. */}
        <div className="platform-tabs" role="tablist" aria-label="Platform">
          {([
            ['all', 'All', platforms.total?.installs],
            ['ios', 'iOS', platforms.ios?.installs],
            ['android', 'Android', platforms.android?.installs],
          ] as const).map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={platformTab === key}
              className={`platform-tab${platformTab === key ? ' is-active' : ''}`}
              onClick={() => setPlatformTab(key as any)}
            >
              {key !== 'all' && <i className="platform-tab-dot"
                                   style={{ background: key === 'ios' ? IOS_COLOR : ANDROID_COLOR }} />}
              {label}
              <em>{fmt(count)}</em>
            </button>
          ))}
        </div>

        {platformLoading && !platforms?.views
          ? <PlatformSkeleton />
          : rangeKey === 'custom' && !activeRange
          ? <div className="platform-hint">Pick a start and end date to load this range.</div>
          : (() => {
            const view = platforms.views?.[platformTab] ?? platforms.views?.all ?? {};
            const isAll = platformTab === 'all';
            return <>
              {/* Headline numbers first: the six a non-specialist actually needs,
                  each with a plain-English note explaining what it counts. */}
              <div className="kpi-grid">
                <KpiTile label="Installs" value={fmt(view.installs)} help={KPI_HELP.installs}
                         sub={isAll ? `${fmt(platforms.ios?.installs)} iOS · ${fmt(platforms.android?.installs)} Android` : undefined} />
                <KpiTile label="Active users" value={fmt(view.activeUsers)} help={KPI_HELP.activeUsers}
                         sub={platforms.activeNow != null ? `${fmt(isAll ? platforms.activeNow.total : platforms.activeNow[platformTab])} right now` : undefined} />
                <KpiTile label="New users" value={fmt(view.newUsers)} help={KPI_HELP.newUsers} />
                <KpiTile label="Sessions" value={fmt(view.sessions)} help={KPI_HELP.sessions}
                         sub={view.engagement ? `${view.engagement.screensPerSession} screens per visit` : undefined} />
                <KpiTile label="Time in app" value={fmtDuration(view.engagement?.engagementPerUserSeconds)}
                         help={KPI_HELP.engagement} sub="per active user" />
                <KpiTile label="Quality visits" value={`${view.engagement?.engagedSessionRate ?? 0}%`}
                         help={KPI_HELP.engagedRate} sub="of sessions were real use" />
                <KpiTile label="Returning users" value={fmt(view.audience?.returning)} help={KPI_HELP.returning}
                         sub={view.audience?.returningSessions
                           ? `${fmt(view.audience.returningSessions)} of ${fmt(view.sessions)} visits`
                           : undefined} />
                <KpiTile label="Revenue" value={view.engagement?.revenue ? `$${fmt(view.engagement.revenue)}` : '$0'}
                         help={KPI_HELP.revenue}
                         sub={view.activeUsers && view.engagement?.revenue
                           ? `$${(view.engagement.revenue / view.activeUsers).toFixed(2)} per active user`
                           : undefined} />
              </div>

              <div className="chart-grid">
                <div className="chart-card chart-card--wide">
                  <div className="chart-title">Installs over time<Info text={KPI_HELP.installs} /></div>
                  <TrendChart daily={view.daily} compare={isAll} />
                </div>
                <div className="chart-card">
                  <div className="chart-title">
                    {isAll ? 'iOS vs Android' : platformTab === 'ios' ? 'iOS at a glance' : 'Android at a glance'}
                    <Info text="Share of installs from each app store in this period." />
                  </div>
                  {platforms.activeNow?.byMinute &&
                    <LiveMinutes byMinute={platforms.activeNow.byMinute} total={isAll ? platforms.activeNow.total : platforms.activeNow[platformTab]} />}
                  {isAll
                    ? <SplitBar ios={platforms.ios?.installs || 0} android={platforms.android?.installs || 0} unit="installs" />
                    : <dl className="glance">
                        <div><dt>Revenue</dt><dd>{view.engagement?.revenue ? `$${fmt(view.engagement.revenue)}` : '—'}</dd></div>
                        <div><dt>Screens viewed</dt><dd>{fmt(view.engagement?.screenViews)}</dd></div>
                        <div><dt>Quality visits</dt><dd>{fmt(view.engagement?.engagedSessions)}</dd></div>
                        <div><dt>Most-used version</dt><dd>{view.appVersions?.[0]?.name ?? '—'}</dd></div>
                        <div><dt>Most-used phone</dt><dd>{view.devices?.[0]?.name ?? '—'}</dd></div>
                      </dl>}
                  {/* A bare 0 and a token 1 mean the same thing here: Android is
                      not really reporting. The lifetime total is a single test
                      device from Jan 2025, so a share threshold — not `=== 0` —
                      is what separates "live" from "not live yet". */}
                  <div className="chart-note">
                    {!isAll ? (
                      platformTab === 'android' && (platforms.android?.installs || 0) <= 1
                        ? 'This is all Android has ever sent — a single test device in Jan 2025. Real numbers start once the submitted build is live.'
                        : `Figures on this tab count ${platformTab === 'ios' ? 'iOS' : 'Android'} only.`
                    ) : (() => {
                      const ios = platforms.ios?.installs || 0;
                      const android = platforms.android?.installs || 0;
                      const share = ios + android ? (android / (ios + android)) * 100 : 0;
                      if (android === 0 || share < 1) {
                        return `Android is not reporting yet${android ? ` (${android} historic install)` : ''} — no released Android build carries Firebase Analytics. It will start filling in on its own once the submitted version goes live.`;
                      }
                      return 'Both platforms are reporting.';
                    })()}
                  </div>
                </div>
              </div>

              {view.geo &&
                <div className="geo-block">
                  <div className="geo-block-head">
                    Where the {fmt(view.geo.total)} install{view.geo.total === 1 ? '' : 's'} came from{isAll ? '' : platformTab === 'ios' ? ' (iOS)' : ' (Android)'}
                  </div>
                  <div className="geo-grid">
                    <RankedList title="Country" rows={view.geo.countries} compare={isAll} unit="installs" />
                    <RankedList title="State / region" rows={view.geo.regions} compare={isAll} unit="installs" />
                    <RankedList title="City" rows={view.geo.cities} compare={isAll} unit="installs"
                                help="Inferred from IP address, so it is approximate — a VPN or mobile carrier can misplace it." />
                  </div>
                </div>
              }

              <div className="geo-block">
                <div className="geo-block-head">Devices, versions and what people do</div>
                <div className="geo-grid">
                  <RankedList title="Phones used" rows={view.devices} compare={isAll} valueKey="value" unit="devices"
                              help="Active users by handset. Apple reports a hardware code; the familiar model name is shown where it is known." />
                  <RankedList title="App versions in use" rows={view.appVersions} compare={isAll} valueKey="value" unit="versions"
                              help="Active users per build. A long tail on old versions means people are not updating." />
                  {/* Raw GA4 names are swapped for plain English up front; the
                      developer name stays in the tooltip for anyone who needs it. */}
                  <RankedList title="What happens in the app" unit="events"
                              rows={(view.events || []).map((e: any) => ({
                                ...e,
                                name: EVENT_LABELS[e.name]?.label || e.name,
                                raw: e.name,
                              }))}
                              valueKey="value"
                              compare={isAll}
                              help="Every action the app reports, biggest first."
                              describe={(label) => {
                                const hit = Object.values(EVENT_LABELS).find((v) => v.label === label);
                                return hit ? hit.help : undefined;
                              }} />
                </div>
                <div className="geo-grid geo-grid--pair">
                  <RankedList title="Phone OS versions" rows={view.osVersions} valueKey="value" unit="versions" compare={isAll}
                              help="Which operating system builds people are on. Useful when deciding the oldest OS the app still has to support." />
                  <RankedList title="Languages" rows={view.languages} valueKey="value" unit="languages" compare={isAll}
                              help="The language each phone is set to — not what the app is translated into." />
                </div>
                <div className="platform-hint">
                  Hover any row for what it means. Screen views and active-use pings dominate every
                  app's event count — installs and purchases are the numbers that move the business.
                </div>
              </div>
            </>;
          })()
        }

              {platforms.retention &&
                <div className="geo-block">
                  <div className="geo-block-head">
                    Do people come back?<Info text="Retention always covers the last six weeks, whatever date filter is selected above — coming back is measured against how old a group is, not against a reporting window." />
                  </div>
                  <div className="chart-card">
                    <RetentionGrid retention={platforms.retention} />
                  </div>
                </div>
              }

        {/* GA4 processes app data on a delay, so today and often yesterday read
            low or zero in the reporting API even while the app is busy. Saying
            this beats a viewer reading a lagging 0 as "no installs today". */}
        <div className="platform-hint">
          Installs, users and sessions come from GA4 reporting, which lags app data by up to ~24h —
          today and yesterday may be incomplete. "Active now" is live.
        </div>
      </div>
      }
    </div>
  </section>
  }
</>
  );
};

export default LiveDashboard;